
import { computed, defineComponent, Ref, ref } from 'vue';
import utilities from '@/shared/utilities.service';
import siteContextStore from '@/shared/content/site-context.store';
import { ValidateTokenResultType } from '@/b2c/shared/api/server-apis';
import Deferred from '@/core/async/Deferred';
import { Form } from 'vee-validate';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import authService from '@/b2c/shared/auth/auth.service';
import Recaptcha, { RecaptchaComponentInstance } from '@/shared/form/recaptcha/Recaptcha.vue';

export default defineComponent({
    name: 'ProfileResetPassword',
    components: { Recaptcha, Form },
    setup() {
        const labels = useBlockLabels();
        const confirmationState: Ref<ValidateTokenResultType> = ref(ValidateTokenResultType.InvalidToken);
        const showConfirmation: Ref<boolean> = ref(false);
        const resetToken: Ref<string> = ref('');
        const tokenFromQueryParam = useRouteQuery<string>(QueryKeys.ResetToken);
        const resendResetEmailDone: Ref<boolean> = ref(false);
        const confirmationAnimationTiming: number = 300;
        const resetConfirmationTiming: number = 1500;

        const recaptcha: Ref<RecaptchaComponentInstance | null> = ref(null);
        const recaptchaKey: Ref<string> = ref('');

        const confirmationTitle = computed(() => {
            const titleLabels = {
                [ValidateTokenResultType.Ok]: 'resetpasswordconfirmationok',
                [ValidateTokenResultType.InvalidToken]: 'resetpasswordconfirmationinvalid',
                [ValidateTokenResultType.UserDoesNotExist]: 'resetpasswordconfirmationnouser',
                [ValidateTokenResultType.NoResetTokenForUser]: 'resetpasswordconfirmationnotoken',
                [ValidateTokenResultType.TokenExpired]: 'resetpasswordconfirmationexpired',
            };
            const titleLabel = titleLabels[confirmationState.value];
            return labels[titleLabel];
        });

        const isResetSuccessConfirmation = computed(() => {
            return confirmationState.value === ValidateTokenResultType.Ok;
        });

        const loginLink = computed(() => {
            return siteContextStore.settings.loginLink;
        });

        const setResetToken = (token): void => {
            if (token) {
                resetToken.value = token;
                validateResetToken(token);
            } else {
                showConfirmation.value = true;
            }
        };

        const validateResetToken = async(token: string) => {
            const response = await authService.validateResetPasswordToken(token);
            const resultType = response?.result ?? ValidateTokenResultType.InvalidToken;
            if (resultType !== ValidateTokenResultType.Ok) {
                confirmationState.value = resultType;
                showConfirmation.value = true;
            }
        };

        const doShowConfirmation = (): void => {
            showConfirmation.value = true;
        };

        const verifyRecaptcha = () => {
            recaptcha.value && recaptcha.value.reset();
            recaptcha.value && recaptcha.value.execute();
        };

        const resendResetEmail = async({ email }): Promise<any> => {
            const deferred = new Deferred<any>();
            const animationTiming = utilities.delay(resetConfirmationTiming);
            const response = authService.resetPasswordTokenByEmail(email, recaptchaKey.value);
            await Promise.all([
                animationTiming,
                response,
            ]);
            setTimeout(() => {
                resendResetEmailDone.value = true;
                deferred.resolve();
            }, confirmationAnimationTiming + 50);
            return deferred.promise;
        };

        const reset = async({ password, passwordConfirm }): Promise<any> => {
            const deferred = new Deferred<any>();
            const animationTiming = utilities.delay(resetConfirmationTiming);
            const response = authService.resetPassword(resetToken.value, password, passwordConfirm);
            await Promise.all([
                animationTiming,
                response,
            ]);
            const serverConfirmationState = await response;
            confirmationState.value = serverConfirmationState?.result ?? ValidateTokenResultType.InvalidToken;
            setTimeout(() => {
                doShowConfirmation();
                deferred.resolve();
            }, confirmationAnimationTiming + 50);
            return deferred.promise;
        };

        setResetToken(tokenFromQueryParam.value);

        const formValidation = {
            email: 'required|email',
            password: 'required|min-password:8',
            passwordConfirm: 'required|same-password:@password',
        };

        return {
            labels,
            showConfirmation,
            resendResetEmailDone,
            loginLink,
            recaptcha,
            recaptchaKey,
            verifyRecaptcha,
            isResetSuccessConfirmation,
            confirmationTitle,
            confirmationAnimationTiming,
            resendResetEmail,
            reset,
            formValidation,
        };
    },
});
